import { MACHINE_TYPES, MachineId } from "../../game/Machine";

/**
 * Bench art, by machine type.
 *
 * Each table ships as three exports off one drawing:
 *
 * - `-top` — the laminated top, filling the footprint edge to edge. This
 *   is the layer that must measure exactly: tables pushed together are
 *   one bench (`src/game/bench-work/bench-group.ts`), so any inset, rounded corner, or
 *   overhang draws a gap down a seam that isn't there.
 * - `-shadow` — the cast shadow, on a canvas wider than the top so it can
 *   bleed. Drawn in a pass of its own *under every table's top*
 *   (`WorktableShadowLayer`), because a neighbour's shadow falling across
 *   the top you just butted against it is exactly the seam this is
 *   avoiding.
 * - `-complete` — the two flattened together, for icons and anywhere a
 *   table is shown on its own.
 *
 * `@4x` is the close-up export used by the bench view, where a bench is
 * on screen at roughly native size instead of an eighth of it: the same
 * drawing off the same canvas centre with four times the pixels, so
 * swapping one for the other lands the art in the same place. PIXI reads
 * the suffix as a resolution, so it still measures the pipeline's 8 px
 * to the inch and scales by the same divisor as the 1x file — it is
 * sharper, not bigger.
 *
 * A table with no entry here falls back to the procedural sprite, which
 * is what the shop floor drew for every table until now — so art can land
 * one table at a time. Both tables have art today.
 *
 * These are deliberately NOT in `scripts/trim-images.ts`, and they don't
 * need to be. The tops have no margin to trim at all — they're opaque
 * corner to corner, which is the whole point of them. The shadows and the
 * completes do carry a transparent margin, and trimming them would in
 * fact be safe (the script keeps every pixel with any alpha at all, so
 * the soft halo survives, and it crops symmetrically about the canvas
 * centre, which is exactly the registration these rely on) — it would
 * just save a couple of kilobytes and make the layers' shared geometry
 * harder to see. Left untrimmed so a top and its shadow can be compared
 * by their canvases.
 */
const WORKTABLE_ART: Partial<Record<MachineId, string>> = {
  worktable1x1: "workbench-2x2",
  worktable1x2: "workbench-2x4",
};

export type WorktableLayer = "top" | "shadow" | "complete";

/** The art path for one layer of a table, or null when it isn't drawn yet.
 * `zoomed` picks the 32 px/inch export the bench view leans on. */
export function worktableArtSrc(
  machineId: string,
  layer: WorktableLayer,
  zoomed = false,
): string | null {
  const base = WORKTABLE_ART[machineId as MachineId];
  return base ? `/images/${base}-${layer}${zoomed ? "@4x" : ""}.png` : null;
}

/**
 * The makeshift bench, which is not a worktable and ships as one
 * flattened drawing rather than layers: the shop floor's copy
 * (`SimpleStationArt`) and the close-up the bench view leans over.
 */
export const MAKESHIFT_BENCH_ART = "/images/makeshift-bench.png";
export const MAKESHIFT_BENCH_CLOSE_UP = "/images/makeshift-bench@4x.png";

/**
 * The close-up art the bench view draws one table of the open run with:
 * the same drawing the floor uses, off its `@4x` export. Tables carry a
 * shadow layer of their own; the makeshift bench is a single flattened
 * drawing and has none. Null for a bench with no art, which the view
 * draws as a plain top instead.
 */
export function benchCloseUpArt(
  machineId: string,
): { top: string; shadow: string | null } | null {
  const top = worktableArtSrc(machineId, "top", true);
  if (top) {
    return { top, shadow: worktableArtSrc(machineId, "shadow", true) };
  }
  if (machineId === MACHINE_TYPES.workspace.id) {
    return { top: MAKESHIFT_BENCH_CLOSE_UP, shadow: null };
  }
  return null;
}

/** Every worktable art file, for the texture preloader. */
export function worktableArtPaths(): string[] {
  const layers: WorktableLayer[] = ["top", "shadow", "complete"];
  return Object.keys(WORKTABLE_ART).flatMap((id) =>
    layers.flatMap((layer) => [
      worktableArtSrc(id, layer)!,
      worktableArtSrc(id, layer, true)!,
    ]),
  );
}
